import { VideoReviewStatus } from "@components/videos/enums/VideoReviewStatus.enum";
import { VideoReviewSubStatus } from "@components/videos/enums/VideoReviewSubStatus.enum";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell, VeriTableRowStatus } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { DateRange } from "@enums/DateRange.enum";
import { IDriver } from "@interfaces/IDriver";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { IVideoEventCategory } from "@interfaces/IVideo";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { IObservationFilter } from "@interfaces/filters/IObservationFilter";
import { IVehicleFilter } from "@interfaces/filters/IVehicleFilter";
import { ColourService } from "@services/colour.service";
import { ObservationsService } from "@services/observations.service";
import { TranslationService } from "@services/translation.service";
import { UsersService } from "@services/users.service";
import { IMarkerOptions } from "@typings/google.maps.angular";
import * as angular from "angular";
import { CameraDisplayService } from "../cameraDisplay.service";
import { VideoObservation } from "../enums/VideoObservation";
import { VideoStatus } from "../enums/VideoStatus";
import { IVideoEntry } from "../models/IVideoEntry";
import { VideoEntryService } from "../services/video-entry.service";
import { VideoReviewStatusService } from "../services/video-review-status.service";
import { VideoReviewService } from "../services/video-review.service";
import "./videoCoachingPage.component.scss";

interface IComponentBindings {
    vehicle: IVehicle;
    driver: IDriver;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    vehicle: IVehicle;
    driver: IDriver;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IVideoEntry>[] = [];

    videoEvents: IVideoEntry[];
    displayVideoEvents: IVideoEntry[];
    veriMap: IVeriMapComponent;
    eventMarkerOptions: IMarkerOptions<IVideoEntry>;
    eventCategories: IVideoEventCategory[] = [];

    defaultDateRange: DateRange = DateRange.Latest7Days;

    showMinorEvents: boolean;

    dateFilter: IDateFilter;
    driverFilter: IDriverFilter;
    vehicleFilter: IVehicleFilter;
    groupFilter: IGroupFilter;

    loading: boolean;

    users: IUser[];

    statusToFilter: string;

    static $inject = ["$q", "$scope", "cameraDisplayService", "veriTableService", "usersService", "observationsService", "colourService", "translationService", "videoReviewService", "videoReviewStatusService", "videoEntryService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly veriTableService: VeriTableService,
        private readonly usersService: UsersService,
        private readonly observationsService: ObservationsService,
        private readonly colourService: ColourService,
        private readonly translationService: TranslationService,
        private readonly videoReviewService: VideoReviewService,
        private readonly videoReviewStatusService: VideoReviewStatusService,
        private readonly videoEntryService: VideoEntryService
    ) { }

    $onInit() {
        this.createTableHeaders();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (!onChangesObj["driver"]?.isFirstChange() || !onChangesObj["vehicle"]?.isFirstChange()) {
            if (onChangesObj["vehicle"] || onChangesObj["driver"]) {
                this.loadData();
            }
        }
    }

    videoEventClicked(video: IVideoEntry, $event: MouseEvent) {
        this.cameraDisplayService.showVideoPlayer(video, $event, true).then(() => {
            this.loadData(true);
        });
    }

    dateFilterChanged(filter: IDateFilter) {
        this.dateFilter = filter;
        this.loadData(true);
    }

    vehicleFilterChanged(filter: IVehicleFilter) {
        this.vehicleFilter = filter;
        this.loadData();
    }

    driverFilterChanged(filter: IDriverFilter) {
        this.driverFilter = filter;
        this.loadData();
    }

    observationsFilterChanged(filter: IObservationFilter) {
        this.displayVideoEvents = [];

        if (!filter.observation) {
            this.displayVideoEvents = this.videoEvents;
        }
        else {
            this.filterVideoEventsByObservations(filter);
        }

        this.createTableRows();
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent) {
        this.veriMap = veriMap;
    }

    showMinorEventsClicked() {
        this.createTableRows();
    }
    searchString: string;
    onSearch(searchString) {
        this.searchString = searchString.toLocaleLowerCase();
        //this.filterVideos();
        this.$scope.$applyAsync();
    }

    // ---

    private createTableHeaders() {
        this.tableHeaders = [
            this.veriTableService.createDateTimeHeader(this.translationService.t("VideoCoachingPage.EventTime"), { width: 160 }),
            this.veriTableService.createDateTimeHeader(this.translationService.t("VideoCoachingPage.CoachingDate")),
            { title: this.translationService.t("VideoCoachingPage.CoachedBy"), sortable: true },
            this.veriTableService.createVehicleRegHeader(),
            { title: this.translationService.t("Common.Driver"), sortable: true },
            { title: this.translationService.t("Videos.Observations"), sortable: true },
            { title: this.translationService.t("Videos.ReviewStatus"), width: 140, sortable: true, includeInSummary: true },
            { title: this.translationService.t("Videos.CoachingOutcomes"), width: 160, sortable: true, includeInSummary: true, id: "CoachingOutcomes" },
        ];

        if (this.vehicle) {
            this.tableHeaders.splice(1, 1);
        }

        if (this.driver) {
            this.tableHeaders.splice(2, 1);
        }
    }

    private createTableRows(): void {
        this.tableRows = this.veriTableService.createTableRows(this.displayVideoEvents, data => this.createTableRow(data));
    }

    private createTableRow(videoEvent: IVideoEntry): IVeriTableRow<IVideoEntry> {

        const rowStatus = this.getRowStatusForVideoReviewStatus(videoEvent);

        const row: IVeriTableRow<IVideoEntry> = {
            cells: this.createTableRowCells(videoEvent),
            data: videoEvent,
            rowWarningStatus: rowStatus,
            rowClassName: this.veriTableService.getRowClassNameFromRowStatus(rowStatus),
            eventSummaryColour: this.getEventSummaryColour(videoEvent)
        };

        if (this.vehicle) {
            row.cells.splice(1, 1);
        }

        if (this.driver) {
            row.cells.splice(2, 1);
        }

        return row;
    }

    // look into this...
    private getEventSummaryColour(videoEvent: IVideoEntry) {
        switch (videoEvent.reviewStatus) {
            case VideoReviewStatus.Coached:
                return this.colourService.greenColor;

            case VideoReviewStatus.Reviewed:
                if (videoEvent.reviewSubStatus == VideoReviewSubStatus.NoActionRequired) {
                    return this.colourService.greenColor;
                }
                else {
                    return this.colourService.orangeColor;
                }

            default: return this.colourService.greyColor;
        }
    }

    private createTableRowCells(videoEvent: IVideoEntry): IVeriTableRowCell[] {
        let lastCoachingEvent = videoEvent.MetaData.Notes?.length
            ? videoEvent.MetaData.Notes[videoEvent.MetaData.Notes.length - 1]
            : null;

        // If the status is needs review, then blank it out
        if (lastCoachingEvent && lastCoachingEvent.Status == VideoReviewStatus.NeedsReview) {
            lastCoachingEvent = null;
        }

        let coachingEventTimeCell: IVeriTableRowCell = { contents: "N/A", className: "text-muted", sortData: new Date(8640000000000000) };
        let coachingUserCell: IVeriTableRowCell = { contents: "N/A", className: "text-muted", sortData: null };

        if (lastCoachingEvent?.Status == 1 && lastCoachingEvent?.ReviewStatus == 1 || lastCoachingEvent?.Status == 2 /*|| true*/) {
            coachingEventTimeCell = this.veriTableService.createDateTimeCell(lastCoachingEvent.ActionDate);

            const matchingUser = this.usersService.findUser(this.users, lastCoachingEvent?.UserId);
            const username = matchingUser?.CurrentAccountUserMapping?.Name || "N/A";

            coachingUserCell = {
                contents: `<span class="layout-row layout-align-start-center"><i class="material-icons text-muted s14">person</i> ${username}</span>`,
                exportData: username
            };
        }

        return [
            this.veriTableService.createDateTimeCell(videoEvent.VideoStartTime),
            coachingEventTimeCell,
            coachingUserCell,
            this.veriTableService.createVehicleRegCell(videoEvent.vehicle),
            this.veriTableService.createDriverCell(videoEvent.driver),
            { contents: videoEvent.MetaData?.VideoObservations.reduce((x, y) => x ? `${x}, ${this.observationsService.setObservationTitleForDisplay(y)}` : this.observationsService.setObservationTitleForDisplay(y), "") },
            {
                contents: this.videoReviewStatusService.getReviewDisplayStatus(videoEvent.reviewStatus, videoEvent.reviewSubStatus),
                data: videoEvent.reviewStatus,
                summaryLabel: this.videoReviewStatusService.getReviewDisplayStatus(videoEvent.reviewStatus, videoEvent.reviewSubStatus),
                summaryIcon: this.videoReviewService.getReviewStatusIcon(videoEvent.reviewStatus),
            },
            {
                contents: videoEvent.reviewStatus == VideoReviewStatus.NeedsReview || videoEvent.reviewStatus == VideoReviewStatus.Reviewed
                    ? this.videoReviewStatusService.getReviewSubDisplayStatus(videoEvent.reviewSubStatus)
                    : this.videoReviewStatusService.getCoachingDisplayStatus(videoEvent.coachingStatus)
            }
        ];
    }

    private filterVideoEventsByObservations(filter: IObservationFilter) {
        this.videoEvents.forEach(videoEvent => {
            const foundObservation = videoEvent.MetaData?.VideoObservations.find(vc => vc == VideoObservation[filter.observation.label]);

            if (isNaN(foundObservation)) {
                return;
            }

            this.displayVideoEvents.push(videoEvent);
        });
    }

    private loadData(invalidateCache = false) {
        this.loading = true;

        const usersPromise = this.usersService.getUsers({ includeAdmins: true }, invalidateCache);
        const videoEventsPromise = this.getVideoEvents(invalidateCache);


        this.$q.all([usersPromise, videoEventsPromise])
            .then(([users, videoEvents]) => {
                this.users = users;

                this.videoEvents = videoEvents;
                this.displayVideoEvents = videoEvents;

                if (this.statusToFilter) {
                    this.displayVideoEvents = videoEvents.filter(videoEvent => videoEvent.reviewStatusDisplayValue == this.statusToFilter);
                }

                this.createTableRows();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private getVideoEvents(invalidateCache = false): angular.IPromise<IVideoEntry[]> {

        if (!this.dateFilter) {
            return this.$q.when([]);
        }

        if (this.vehicle) {
            this.vehicleFilter = { vehicle: this.vehicle };
        }

        if (this.driver) {
            this.driverFilter = { driver: this.driver };
        }

        const promise = this.videoEntryService.getVideosEntriesWithMappings(
            this.dateFilter.start,
            this.dateFilter.end,
            {
                vehicleFilter: this.vehicleFilter,
                driverFilter: this.driverFilter,
                groupFilter: this.groupFilter,
                processEntries: true,
                invalidateCache: invalidateCache
            }
        ) as angular.IPromise<IVideoEntry[]>;

        return promise.then(videos => {
                return videos.filter(v => v.Status == VideoStatus.Downloaded);
            });
    }

    private getRowStatusForVideoReviewStatus(video: IVideoEntry): VeriTableRowStatus {
        if (video.reviewStatus == VideoReviewStatus.Coached ||
            video.reviewStatus == VideoReviewStatus.Reviewed && video.reviewSubStatus === VideoReviewSubStatus.NoActionRequired) {
            return VeriTableRowStatus.OkChecked;
        }

        if (video.reviewStatus == VideoReviewStatus.Reviewed && video.reviewSubStatus === VideoReviewSubStatus.ActionRequired) {
            return VeriTableRowStatus.Warning;
        }

        if (video.reviewStatus == VideoReviewStatus.NeedsReview) {
            return VeriTableRowStatus.OkDash;
        }

        return VeriTableRowStatus.OK;
    }
}

export class VideoCoachingPageComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            vehicle: "<?",
            driver: "<?",
            statusToFilter: "<"
        };

        this.controller = ComponentController;
        this.template = require("./videoCoachingPage.component.html");
    }
}
